import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, CheckCircle2, UserPlus, ShieldX, XCircle, Mail, Phone, Building2 } from "lucide-react";
import { BrutalButton, BrutalCard } from "@/components/Brutal";
import { GroupMultiSelect } from "@/components/Selects";
import { useAuth } from "@/context/AuthContext";
import { makeT } from "@/lib/i18n";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";

export default function Goedkeuren() {
  const { reqId } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const lang = localStorage.getItem("slackr_lang") || "nl";
  const t = makeT(lang);

  const [state, setState] = useState("loading"); // loading | ready | forbidden | notfound | handled | done
  const [req, setReq] = useState(null);
  const [groupIds, setGroupIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // "approved" | "rejected"

  useEffect(() => {
    if (loading) return;
    if (user === false) {
      navigate(`/login?redirect=${encodeURIComponent(`/goedkeuren/${reqId}`)}`, { replace: true });
      return;
    }
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get(`/join-requests/${reqId}`);
        if (data.status && data.status !== "pending") { setState("handled"); return; }
        setReq(data);
        setState("ready");
      } catch (e) {
        const s = e.response?.status;
        if (s === 403) setState("forbidden");
        else setState("notfound");
      }
    })();
  }, [user, loading, reqId, navigate]);

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/join-requests/${reqId}/approve`, { group_ids: groupIds, base_url: window.location.origin });
      toast.success(t("Goedgekeurd 🎉"));
      setResult("approved");
      setState("done");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await api.post(`/join-requests/${reqId}/reject`, {});
      toast.success(t("Afgewezen"));
      setResult("rejected");
      setState("done");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-brand-cream flex items-center justify-center p-5" data-testid="goedkeuren-page">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-4">
          <img src="/icon-192.png" alt="Slackr" className="w-14 h-14 rounded-2xl brutal-sm mb-2" />
          <h1 className="font-head font-black text-2xl">Slackr</h1>
        </div>
        <BrutalCard>
          {(state === "loading") && (
            <div className="text-center py-10" data-testid="goedkeuren-loading">
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-brand-violet" />
            </div>
          )}

          {state === "forbidden" && (
            <div className="text-center py-6 space-y-2" data-testid="goedkeuren-forbidden">
              <ShieldX className="w-12 h-12 mx-auto text-brand-ink/40" strokeWidth={2.5} />
              <h2 className="font-head font-black text-xl">{t("Geen toegang")}</h2>
              <p className="text-brand-ink/60 font-semibold">{t("Alleen een beheerder van deze organisatie kan dit verzoek behandelen.")}</p>
              <BrutalButton onClick={() => navigate("/vandaag")} className="mt-3">{t("Naar Slackr")}</BrutalButton>
            </div>
          )}

          {state === "notfound" && (
            <div className="text-center py-6 space-y-2" data-testid="goedkeuren-notfound">
              <XCircle className="w-12 h-12 mx-auto text-brand-ink/40" strokeWidth={2.5} />
              <h2 className="font-head font-black text-xl">{t("Verzoek niet gevonden")}</h2>
              <p className="text-brand-ink/60 font-semibold">{t("Deze aanmelding bestaat niet meer.")}</p>
              <BrutalButton onClick={() => navigate("/instellingen")} className="mt-3">{t("Naar instellingen")}</BrutalButton>
            </div>
          )}

          {state === "handled" && (
            <div className="text-center py-6 space-y-2" data-testid="goedkeuren-handled">
              <CheckCircle2 className="w-12 h-12 mx-auto text-brand-green" strokeWidth={2.5} />
              <h2 className="font-head font-black text-xl">{t("Al afgehandeld")}</h2>
              <p className="text-brand-ink/60 font-semibold">{t("Deze aanmelding is al goedgekeurd of afgewezen.")}</p>
              <BrutalButton onClick={() => navigate("/instellingen")} className="mt-3">{t("Naar instellingen")}</BrutalButton>
            </div>
          )}

          {state === "done" && (
            <div className="text-center py-6 space-y-2" data-testid="goedkeuren-done">
              {result === "approved"
                ? <CheckCircle2 className="w-14 h-14 mx-auto text-brand-green" strokeWidth={2.5} />
                : <XCircle className="w-14 h-14 mx-auto text-brand-ink/40" strokeWidth={2.5} />}
              <h2 className="font-head font-black text-xl">
                {result === "approved" ? t("Goedgekeurd!") : t("Afgewezen")}
              </h2>
              <p className="text-brand-ink/60 font-semibold">
                {result === "approved"
                  ? t("De aanvrager is toegevoegd aan de organisatie.")
                  : t("De aanmelding is afgewezen.")}
              </p>
              <BrutalButton onClick={() => navigate("/instellingen")} className="mt-3">{t("Naar instellingen")}</BrutalButton>
            </div>
          )}

          {state === "ready" && req && (
            <div className="space-y-3" data-testid="goedkeuren-ready">
              <div className="flex items-center gap-2">
                <UserPlus className="w-5 h-5" strokeWidth={2.5} />
                <span className="font-head font-bold">{t("Nieuwe aanmelding")}</span>
              </div>
              <div className="brutal-sm rounded-xl bg-brand-cream p-3 space-y-1">
                <p className="font-head font-black text-lg" data-testid="goedkeuren-name">{req.name || t("Onbekend")}</p>
                {req.email && <p className="text-sm text-brand-ink/70 font-semibold flex items-center gap-1.5"><Mail className="w-4 h-4" strokeWidth={2.5} />{req.email}</p>}
                {req.phone && <p className="text-sm text-brand-ink/70 font-semibold flex items-center gap-1.5"><Phone className="w-4 h-4" strokeWidth={2.5} />{req.phone}</p>}
                <p className="text-sm text-brand-ink/70 font-semibold flex items-center gap-1.5 pt-1"><Building2 className="w-4 h-4" strokeWidth={2.5} />{t("Wil aansluiten bij")} <b>{req.org_name}</b></p>
              </div>

              {(req.groups?.length > 0) && (
                <div>
                  <p className="text-xs font-bold mb-1.5">{t("Koppel aan groep(en):")}</p>
                  <GroupMultiSelect groups={req.groups} value={groupIds} onChange={setGroupIds} testid="goedkeuren-groups" />
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <BrutalButton data-testid="goedkeuren-approve" variant="green" className="flex-1" onClick={approve} disabled={busy}>
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" strokeWidth={2.5} />}{t("Goedkeuren")}
                </BrutalButton>
                <BrutalButton data-testid="goedkeuren-reject" variant="white" onClick={reject} disabled={busy}>{t("Afwijzen")}</BrutalButton>
              </div>
            </div>
          )}
        </BrutalCard>
      </div>
    </div>
  );
}
